// Timeline-window math.
//
// Pure functions for the {start,end} time window the timeline renders over.
// Two modes (LLD 13 §7):
//   - `relative` → window is `[NOW - zoomHours, NOW]`, scrolling with NOW.
//   - `absolute` → window is a fixed `{start,end}` the user dragged or typed.
//
// Any drag/zoom/clip-focus interaction flips to absolute and writes a
// concrete `{start,end}`, so the range control never goes stale.

export const HOUR_MS = 60 * 60 * 1000;
export const DAY_MS = 24 * HOUR_MS;

/** Zoom limits for a window: 1 minute .. 30 days. */
export const MIN_SPAN_MS = 60 * 1000;
export const MAX_SPAN_MS = 30 * DAY_MS;

/** A concrete absolute window; both fields are epoch ms. */
export interface AbsoluteWindow {
  start: number;
  end: number;
}

export type RangeMode = "relative" | "absolute";

/**
 * Resolve a `{mode, win, zoomHours, nowMs}` view-state into a concrete
 * `{start,end}`. Relative mode anchors to `nowMs`; absolute mode returns
 * `win` directly.
 */
export function resolveWindow(args: {
  mode: RangeMode;
  win: AbsoluteWindow | null;
  zoomHours: number;
  nowMs: number;
}): AbsoluteWindow {
  if (args.mode === "absolute" && args.win) return args.win;
  return { start: args.nowMs - args.zoomHours * HOUR_MS, end: args.nowMs };
}

/**
 * Clamp a candidate window's span into [MIN_SPAN_MS, MAX_SPAN_MS],
 * preserving its center. Used by `setWindow` and `zoomBy` so neither
 * can produce a sub-minute or month+ window by accident.
 */
export function clampWindow(start: number, end: number): AbsoluteWindow {
  let s = Math.round(start);
  let e = Math.round(end);
  if (e - s < MIN_SPAN_MS) {
    const m = (s + e) / 2;
    s = Math.round(m - MIN_SPAN_MS / 2);
    e = Math.round(m + MIN_SPAN_MS / 2);
  } else if (e - s > MAX_SPAN_MS) {
    const m = (s + e) / 2;
    s = Math.round(m - MAX_SPAN_MS / 2);
    e = Math.round(m + MAX_SPAN_MS / 2);
  }
  return { start: s, end: e };
}

/** Convert a wall-clock ms to a 0..1 fraction across the window. */
export function xOf(ms: number, win: AbsoluteWindow): number {
  const span = win.end - win.start;
  if (span <= 0) return 0;
  return (ms - win.start) / span;
}

/** Convert a 0..1 fraction back to a wall-clock ms (inverse of xOf). */
export function tOf(frac: number, win: AbsoluteWindow): number {
  return win.start + frac * (win.end - win.start);
}

/**
 * Frame a window around a clip with breathing room on either side so the
 * scrubber has travel room. Mirrors the mock's `focusWindow`: pad by
 * max(35% of duration, 20s).
 */
export function focusWindowAround(
  clipStartMs: number,
  clipDurMs: number,
): AbsoluteWindow {
  const pad = Math.max(clipDurMs * 0.35, 20_000);
  return clampWindow(clipStartMs - pad, clipStartMs + clipDurMs + pad);
}

/**
 * Zoom a window in/out about an anchor fraction (0..1 across the
 * current window). `factor < 1` zooms in; `factor > 1` zooms out.
 * The point under `anchorFrac` keeps the same wall-clock time.
 */
export function zoomBy(
  win: AbsoluteWindow,
  factor: number,
  anchorFrac: number,
): AbsoluteWindow {
  const span = win.end - win.start;
  const a = Math.max(0, Math.min(1, anchorFrac));
  const anchorMs = win.start + a * span;
  // Clamp the candidate span up-front so the anchor recentering math
  // doesn't pull us outside the legal range.
  const nspan = Math.max(MIN_SPAN_MS, Math.min(MAX_SPAN_MS, span * factor));
  return clampWindow(anchorMs - a * nspan, anchorMs + (1 - a) * nspan);
}

/**
 * Format a wall-clock ms as a tick label on the timeline ruler. Density
 * adapts to the window span: long windows show "Jun 23 14h", short
 * windows show "14:23:45".
 */
export function formatTick(ms: number, spanMs: number): string {
  const d = new Date(ms);
  const spanH = spanMs / HOUR_MS;
  if (spanH > 24) {
    return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${d.toLocaleTimeString([], { hour: "2-digit" })}`;
  }
  if (spanH < 0.5) {
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * Format a wall-clock ms for an `<input type="datetime-local">` value
 * ("YYYY-MM-DDTHH:MM:SS"). The browser wants local wall-clock with no
 * trailing Z; we shift by the local TZ offset before slicing.
 */
export function toLocalInput(ms: number): string {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 19);
}

/**
 * Parse an `<input type="datetime-local">` value back to epoch ms. Returns
 * null when empty / unparseable so callers can ignore stray edits.
 */
export function fromLocalInput(v: string): number | null {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}
